import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSignUp, resendConfirmationCode } from '../auth/cognito';

export function VerifyEmailPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const qEmail = params.get('email');
    if (qEmail) setEmail(qEmail);
  }, [params]);

  async function onConfirm(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const emailLc = email.trim().toLowerCase();
      const addressId =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem(`signup:addressId:${emailLc}`)
          : null;
      await confirmSignUp(
        emailLc,
        code.trim(),
        addressId ? { addressId } : undefined,
      );
      try {
        if (addressId) localStorage.removeItem(`signup:addressId:${emailLc}`);
      } catch {
        /* no-op */
      }
      setInfo('Email confirmed. You can now sign in.');
      setTimeout(() => navigate('/login'), 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend(): Promise<void> {
    setError(null);
    setInfo(null);
    setResending(true);
    try {
      await resendConfirmationCode(email.trim().toLowerCase());
      setInfo('Verification code sent. Please check your email.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-black">
      <div className="w-full max-w-md card">
        <h1 className="text-2xl font-semibold text-white">Verify your email</h1>
        <p className="text-sm text-gray-400 mt-1">
          We&rsquo;ve sent a 6-digit code to your email. Enter it below to activate your account.
        </p>

        <form onSubmit={onConfirm} className="mt-6 space-y-4">
          <div>
            <label className="label" htmlFor="verify-email">Email</label>
            <input
              id="verify-email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div>
            <label className="label" htmlFor="verify-code">Verification code</label>
            <input
              id="verify-code"
              className="input tracking-widest text-center"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
              inputMode="numeric"
              required
            />
          </div>

          {error && <div className="text-sm text-red-400">{error}</div>}
          {info && <div className="text-sm text-emerald-400">{info}</div>}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={submitting || !email || code.length < 6}
          >
            {submitting ? 'Confirming…' : 'Confirm email'}
          </button>

          <button
            type="button"
            className="btn-secondary w-full"
            onClick={onResend}
            disabled={resending || !email}
          >
            {resending ? 'Resending…' : 'Resend code'}
          </button>

          <p className="text-xs text-gray-500 pt-2 border-t border-gray-800">
            <Link to="/login" className="text-red-400 hover:text-red-300 hover:underline">
              Back to sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
